import { useMemo, useState } from "react";
import {
  cn,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@semo/shared-ui";
import {
  Search,
  Wallet,
  Clock,
  DollarSign,
  CheckCircle2,
  Info,
  ExternalLink,
} from "@semo/icons";
import { type SupplierSettlementResponseDto } from "@semo/api-contracts";
import { useSettlements } from "../hooks/useSettlements";
import {
  isEmbeddedInIframe,
  requestParentNavigate,
} from "@/utils/embedMessaging";

type Status = SupplierSettlementResponseDto["settlementStatus"];

/**
 * 판매자센터 정산 탭 — **세모에 낼 수수료의 청구 내역**이다.
 *
 * 계약 대금은 발주기관이 공급기업에게 **직접** 지급한다(카드는 공급기업 가맹점으로, 현금은
 * 공급기업 통장으로). 세모는 대금을 만지지 않으므로 이 화면에는 "세모가 언제 얼마를 보낸다"
 * 는 정보가 존재하지 않는다.
 *
 * 종전 화면은 정반대였다 — 정산예정일 달력과 "정산 예정액"(계약금액 − 수수료)을 크게 띄워
 * **세모가 15일 뒤에 945,000원을 지급한다**고 안내했다. 씨마켓 카드결제 건은 이미 공급기업
 * 가맹점으로 전액 입금된 뒤였는데도 그랬다. 그래서 달력 축(`settlementExpectedDate`)과
 * 정산금액(`settlementAmount`)을 화면에서 걷어내고 수수료 축만 남긴다.
 *
 * 탭과 라우트(`/settlements`)는 그대로 둔다 — 알림톡 딥링크와 대시보드 타일이 걸려 있다.
 */

/** 수수료 수납 상태 — 값 집합은 백엔드와 같고 라벨만 수수료 축이다. */
const STATUS_LABEL: Record<Status, string> = {
  WAITING: "확정 대기",
  PENDING: "미수",
  COMPLETED: "수납 완료",
  DEFERRED: "보류",
};

const STATUS_BADGE_CLASS: Record<Status, string> = {
  WAITING: "text-slate-600 border-slate-200 bg-slate-50",
  PENDING: "text-amber-700 border-amber-200 bg-amber-50",
  COMPLETED: "text-green-600 border-green-200 bg-green-50",
  DEFERRED: "text-red-600 border-red-200 bg-red-50",
};

function statusBadge(status: Status) {
  return (
    <Badge variant="outline" className={STATUS_BADGE_CLASS[status]}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

interface SettlementListProps {
  /** DEV 전용: 정산 탭을 샘플 데이터로 미리보기 (/dev/preview?tab=settlements). */
  previewData?: SupplierSettlementResponseDto[];
}

const won = (n: number) => `₩${n.toLocaleString("ko-KR")}`;
const pad = (n: number) => String(n).padStart(2, "0");

type RangeKey = "all" | "thisMonth" | "lastMonth";

const RANGE_PRESETS: readonly { key: RangeKey; label: string }[] = [
  { key: "all", label: "전체" },
  { key: "thisMonth", label: "이번 달" },
  { key: "lastMonth", label: "지난달" },
];

/**
 * 기간 축은 **청구일(계약일)** 이다 — 수수료는 계약이 서는 순간 확정된다.
 * 종전 화면은 정산예정일 기준이었는데, 그 날짜가 폐지돼 이제 비어 있다.
 */
function resolveRange(key: RangeKey): { start: string; end: string } {
  if (key === "all") return { start: "", end: "" };
  const now = new Date();
  const first = new Date(
    now.getFullYear(),
    now.getMonth() + (key === "thisMonth" ? 0 : -1),
    1,
  );
  const y = first.getFullYear();
  const m = first.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  return {
    start: `${y}-${pad(m + 1)}-01`,
    end: `${y}-${pad(m + 1)}-${pad(last)}`,
  };
}

export const SettlementList: React.FC<SettlementListProps> = ({
  previewData,
}) => {
  const { settlements, isLoading, stats, error, refetch } =
    useSettlements(previewData);

  const [rangeKey, setRangeKey] = useState<RangeKey>("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim();
    const { start, end } = resolveRange(rangeKey);

    return settlements
      .filter((it) => {
        if (start || end) {
          const d = it.contractDate.slice(0, 10);
          if (start && d < start) return false;
          if (end && d > end) return false;
        }
        if (
          q &&
          !`${it.contractNumber} ${it.buyerName ?? ""} ${it.projectName ?? ""}`.includes(
            q,
          )
        )
          return false;
        return true;
      })
      .sort((x, y) => y.contractDate.localeCompare(x.contractDate));
  }, [settlements, rangeKey, search]);

  const openCmarketContracts = () => {
    requestParentNavigate("my-contracts");
  };

  if (isLoading) {
    return (
      <div className="py-16 text-center text-sm text-slate-500">
        수수료 청구 내역을 불러오는 중…
      </div>
    );
  }

  // 조회가 실패했는데 빈 목록으로 합계를 내면 «0건·₩0» 이 정상처럼 보인다(A3-15) — 실패를 알린다.
  if (error && !previewData) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="text-sm text-slate-600">
          수수료 청구 내역을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* 대금·계산서가 어디서 오가는지 먼저 말해 준다 — 이 화면에 없는 정보라서 더 필요하다. */}
      <div className="mb-4 rounded-xl border border-primary/15 bg-primary/5 p-4">
        <div className="flex gap-2.5">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-text" />
          <div className="space-y-1.5 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">
              계약 대금은 발주기관에서 직접 받습니다.
            </p>
            <p>
              카드 결제는 귀사 가맹점으로, 현금은 귀사 계좌로 바로 들어옵니다.
              세모를 거치지 않으므로 이 화면에는 대금 입금 예정일이 없습니다.
            </p>
            {isEmbeddedInIframe() ? (
              <p>
                발주기관에 보낼{" "}
                <b className="text-slate-900">거래 세금계산서는 씨마켓</b>의
                &lsquo;나의 계약 관리&rsquo;에서 발행합니다.
              </p>
            ) : (
              <p>
                발주기관에 보낼{" "}
                <b className="text-slate-900">거래 세금계산서</b>는 세모에서
                발행하지 않습니다. 사용하시는 전자세금계산서 서비스에서 발행해
                주세요.
              </p>
            )}
            {isEmbeddedInIframe() && (
              <button
                type="button"
                onClick={openCmarketContracts}
                className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-white px-3 py-1.5 text-xs font-semibold text-primary-text hover:bg-primary/5"
              >
                씨마켓 나의 계약 관리 열기
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            )}
            <p className="pt-1 text-xs text-slate-500">
              아래 목록은 세모에 내실 <b>플랫폼 이용 수수료</b>의 청구
              내역입니다.
            </p>
          </div>
        </div>
      </div>

      {/* KPI — 전부 수수료 축이다. 대금(계약금액 − 수수료)은 세지 않는다. */}
      <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">총 계약 건수</CardTitle>
            <Wallet className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{stats.totalCount}건</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">확정 대기</CardTitle>
            <Clock className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{stats.waitingCount}건</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">미수 수수료</CardTitle>
            <DollarSign className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-amber-600">
              {won(stats.unpaidCommission)}
            </div>
            <p className="text-xs text-muted-foreground">
              아직 내지 않은 수수료
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              누적 납부 수수료
            </CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-green-600">
              {won(stats.collectedCommission)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 필터 — 기간(청구일) + 검색 */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={rangeKey === p.key}
              onClick={() => setRangeKey(p.key)}
              className={cn(
                "rounded-lg px-3 py-1.5 text-sm font-semibold transition-all",
                rangeKey === p.key
                  ? "bg-primary/10 text-primary"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:ml-auto sm:w-64">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="계약번호 또는 발주기관명 검색"
            className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-3 text-sm placeholder:text-slate-400 focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <div className="mb-2 text-xs font-medium text-slate-500">
        조회 결과 <b className="text-slate-700">{filtered.length}건</b>
      </div>

      <div className="overflow-hidden rounded-md border bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="whitespace-nowrap">계약번호</TableHead>
                <TableHead className="whitespace-nowrap">프로젝트명</TableHead>
                <TableHead className="whitespace-nowrap">발주기관</TableHead>
                <TableHead className="whitespace-nowrap">청구일</TableHead>
                <TableHead className="whitespace-nowrap text-right">
                  계약금액
                </TableHead>
                <TableHead className="whitespace-nowrap text-right">
                  수수료 청구액
                  {/* 청구액은 수수료 5%에 부가세 10%를 더한 값(5.5%)이다 (GS A3-08). */}
                  <span className="ml-1 text-xs font-normal text-slate-400">
                    (부가세 포함)
                  </span>
                </TableHead>
                <TableHead className="whitespace-nowrap text-center">
                  납부 상태
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-8 text-center text-slate-500"
                  >
                    조건에 맞는 수수료 청구 내역이 없습니다.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((it) => (
                  <TableRow key={it.id} className="hover:bg-slate-50/50">
                    <TableCell className="font-mono text-xs">
                      {it.contractNumber}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate font-medium">
                      {it.projectName}
                    </TableCell>
                    <TableCell>{it.buyerName}</TableCell>
                    {/* 청구일 = 계약일. 수수료는 계약이 서는 순간 확정된다. */}
                    <TableCell>{it.contractDate}</TableCell>
                    <TableCell className="text-right text-slate-600">
                      {won(it.totalAmount)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">
                      {won(it.commissionAmount)}
                    </TableCell>
                    <TableCell className="text-center">
                      {statusBadge(it.settlementStatus)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
